import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleCheck,
  faEnvelope,
  faFolder,
} from '@fortawesome/pro-duotone-svg-icons';
import { faCheck, faEnvelope as faEnvelopeSolid, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { faClock, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  StatusMarkerIconProps,
  StatusMarkerIconVariant,
} from '../../interfaces/props/StatusMarkerIconProps';
import './styles.css';

const VARIANT_ICONS: Record<Exclude<StatusMarkerIconVariant, 'enviada' | 'revisao'>, IconDefinition> = {
  pendencia: faTriangleExclamation,
  pendenciaEnviada: faClock,
  autorizada: faCircleCheck,
};

const isSuccessEnvelope = (
  variant: StatusMarkerIconVariant,
  state: StatusMarkerIconProps['state'],
): boolean =>
  variant === 'enviada' && (state === 'current' || state === 'completed');

const isRevisaoActive = (
  variant: StatusMarkerIconVariant,
  state: StatusMarkerIconProps['state'],
): boolean =>
  variant === 'revisao' && (state === 'current' || state === 'completed');

const SuccessEnvelopeGlyph: React.FC = () => (
  <span className="status-marker-icon__success-envelope">
    <FontAwesomeIcon
      icon={faEnvelopeSolid as IconProp}
      className="status-marker-icon__success-envelope-glyph"
    />
    <span className="status-marker-icon__success-envelope-badge">
      <FontAwesomeIcon
        icon={faCheck as IconProp}
        className="status-marker-icon__success-envelope-check"
      />
    </span>
  </span>
);

const RevisaoFolderGlyph: React.FC = () => (
  <span className="status-marker-icon__revisao-folder">
    <FontAwesomeIcon
      icon={faFolder as IconProp}
      className="status-marker-icon__revisao-folder-glyph"
    />
    <FontAwesomeIcon
      icon={faMagnifyingGlass as IconProp}
      className="status-marker-icon__revisao-search-glyph"
    />
  </span>
);

const StatusMarkerIcon: React.FC<StatusMarkerIconProps> = ({
  variant,
  state,
  size = 'md',
  className,
  ariaLabel,
}) => {
  const classNames = [
    'status-marker-icon',
    `status-marker-icon--${variant}`,
    `status-marker-icon--${state}`,
    size === 'sm' ? 'status-marker-icon--sm' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const decorativeProps = ariaLabel
    ? { role: 'img' as const, 'aria-label': ariaLabel }
    : { 'aria-hidden': true as const };

  if (isSuccessEnvelope(variant, state)) {
    return (
      <span className={classNames} {...decorativeProps}>
        <SuccessEnvelopeGlyph />
      </span>
    );
  }

  if (isRevisaoActive(variant, state)) {
    return (
      <span className={classNames} {...decorativeProps}>
        <RevisaoFolderGlyph />
      </span>
    );
  }

  const icon =
    variant === 'enviada'
      ? faEnvelope
      : variant === 'revisao'
        ? faFolder
        : VARIANT_ICONS[variant];

  return (
    <span className={classNames} {...decorativeProps}>
      <span className="status-marker-icon__glyph">
        <FontAwesomeIcon icon={icon as IconProp} />
      </span>
    </span>
  );
};

export default StatusMarkerIcon;
